import { type KeyboardEvent, useId, useState } from "react";
import { FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Note, NotSavedNote } from "@/components/ui/Note";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Button } from "@/components/ui/button";
import { useUpdateInventionTitle } from "@/hooks/use-tasks";
import { refusalDetail } from "@/services/api-client";
import { ReviewCard } from "./ReviewCard";

/**
 * Why the title was not saved, and Retry. The draft is generated under the stored title, so a
 * refused save is said, and the words kept in the field count as unsaved while it stands.
 */
function TitleRefused({
  error,
  kept,
  onRetry,
}: {
  error: Error | null;
  kept: boolean;
  onRetry: () => void;
}) {
  useUnsavedDraft(error !== null && kept);
  if (error === null) return null;
  return (
    <>
      <p role="alert" className="mt-2 text-xs text-destructive">
        {refusalDetail(error, "The title could not be saved.")}
      </p>
      <NotSavedNote failed onRetry={onRetry} />
    </>
  );
}

/**
 * The invention's title, which the draft prints, written where it is drawn. It
 * is never the draft's name (its working title). Enter or leaving the line saves
 * a changed title; Escape puts the served one back. While there is none, the
 * card flags it as a gap, says what the draft prints instead (marked for review)
 * and offers the title proposed from the disclosure.
 */
export function TitleCard({
  taskId,
  title,
  proposed,
}: {
  taskId: string;
  title: string | null;
  proposed: string | null;
}) {
  const id = useId();
  const held = title ?? "";
  const [draft, setDraft] = useState(held);
  const retitle = useUpdateInventionTitle();
  const write = (next: string) => {
    retitle.mutate({ taskId, title: next });
  };
  const save = () => {
    const next = draft.replace(/\s+/g, " ").trim();
    if (next === "" || next === held) {
      setDraft(held);
      return;
    }
    write(next);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
    if (event.key === "Escape") setDraft(held);
  };
  return (
    <ReviewCard icon={<FileText />} title="Patent title">
      <MicroLabel as="label" htmlFor={id} className="mb-2 block">
        Title of the invention
      </MicroLabel>
      <Input
        id={id}
        appearance="inline"
        value={draft}
        placeholder="The title the application is filed under"
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={save}
        onKeyDown={onKeyDown}
        className="w-full text-[15px] font-bold"
      />
      {title === null && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2">
          <Note tone="warning">
            The application needs a title of the invention. Until you set one,
            the draft prints{" "}
            {proposed === null
              ? "its working title,"
              : `the title proposed from the disclosure, ${proposed},`}{" "}
            marked for your review.
          </Note>
          {proposed !== null && (
            <Button
              variant="link"
              onClick={() => {
                write(proposed);
              }}
            >
              Use this title
            </Button>
          )}
        </div>
      )}
      <TitleRefused
        error={retitle.isError ? retitle.error : null}
        kept={draft !== held}
        onRetry={save}
      />
    </ReviewCard>
  );
}
